import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { HeaderRendererComponent } from '@frk/eds-components';
import {
  AgGridHelperService,
  ProductColGroupId,
  ProductColId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { ProductData, SuppressDetails } from '@types';
import {
  FundListingPerformanceController,
  FundListingRatingsController,
} from '../controllers';

/**
 * Return default performance column definitions for intl.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getDefaultPerformanceColumnDefsForIntl(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      children: [agGridHelperService.getFundNameColDef()],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController),
        agGridHelperService.getNavChangeValueColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      // to add asOfDate in mobile view
      headerValueGetter: () =>
        `${translateService.instant(
          'pds.PERF_AS_OF_DATE'
        )} ${listingController.getPerformanceAsOf()}`,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly2YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly3YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly4YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly5YrColDef(listingController),
        agGridHelperService.getCalendarYearQuarterlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly2YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly3YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly4YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly5YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearInceptionColDef(),
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly5YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly10YrColDef(listingController),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly5YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly10YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly2YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly3YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly4YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly5YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteQuarterly2YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly3YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly4YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly5YrColDef(listingController),
        agGridHelperService.getDiscreteInceptionColDef(),
        agGridHelperService.getFundDocumentColDef(),
      ],
    },
  ];
  return colGroupDefs;
}

/**
 * Return default identifier column definitions for intl.
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getDefaultIdentifierColumnDefsForIntl(
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColDef[] {
  const columnDefs = [
    agGridHelperService.getFundNameColDef(),
    {
      colId: ProductColId.FUND_CODE,
      headerName: translateService.instant('pds.Fund_Code'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.fundId;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ISIN,
      headerName: translateService.instant('pds.ISIN'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.isin;
      },
      width: 250,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.SEDOL,
      headerName: translateService.instant('pds.SEDOL'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.sedol;
      },
      width: 130,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.BLOOMBERG,
      headerName: translateService.instant('pds.BLOOMBERG_IDENTIFIER'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.bloomberg;
      },
      width: 200,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.SHARE_CLASS_CODE,
      headerName: translateService.instant('pds.SHARE_CLASS_CODE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.shareClassCode;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.COUNTRY_CODE,
      headerName: translateService.instant('pds.CNTRYCODE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.countryCode;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.LANG_CODE,
      headerName: translateService.instant('pds.LANGCODE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.languageCode;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.TA_ID,
      headerName: translateService.instant('pds.TA_ID'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.taid;
      },
      width: 120,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.TICKER,
      headerName: translateService.instant('pds.SECURITY_TICKER'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.ticker;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.CUSIP,
      headerName: translateService.instant('pds.CUSIP'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.cusip;
      },
      width: 170,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.REUTERS,
      headerName: translateService.instant('pds.REUTERS_SYMBOL'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.reuterSystem;
      },
      width: 250,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.MEXID,
      headerName: translateService.instant('pds.MEXID'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.mexId;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.APIR,
      headerName: translateService.instant('pds.APIR'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.apir;
      },
      width: 130,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ARSN,
      headerName: translateService.instant('pds.ARSN'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.arsn;
      },
      width: 140,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ANBIMACODE,
      headerName: translateService.instant('pds.ANBIMA_CODE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.anbimaCode;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.VLN,
      headerName: translateService.instant('pds.VALEREN_SYMBOL'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.vln;
      },
      width: 112,
      autoHeight: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.INTRADAYNAV,
      headerName: translateService.instant('pds.INTRADAYNAV'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.intradayNav;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.RFI,
      headerName: translateService.instant('pds.POLAND_RFI'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.rfi;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ADM,
      headerName: translateService.instant('pds.ADM'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.adm;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.DSC,
      headerName: translateService.instant('pds.DSC'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.dsc;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.IRESS,
      headerName: translateService.instant('pds.IRESS'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.iress;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.IRESSINAVCODE,
      headerName: translateService.instant('pds.IRESSINAVCODE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.iressinavCode;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.FUNDBMTICKER,
      headerName: translateService.instant('pds.FUNDBMTICKER'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.fundbmTicker;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.WKN,
      headerName: translateService.instant('pds.WKN'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.wkn;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.IOPV,
      headerName: translateService.instant('pds.IOPV'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.iopv;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.NASDAQXNMS,
      headerName: translateService.instant('pds.NASDAQXNMS'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.nasdaqxnms;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ML_IDENTIFIER,
      headerName: translateService.instant('pds.MLIDENTIFIER'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.mlidentifier;
      },
      width: 188,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
  ];
  return columnDefs;
}

/**
 * Return default rating column definitions for intl.
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultRatingColumnDefsForIntl(
  listingController: FundListingRatingsController,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: ProductColGroupId.FUND,
      children: [agGridHelperService.getFundNameColDef()],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      headerClass: 'ag-header-group-cell--noflex',
      children: [
        agGridHelperService.getMorningStarCategoryColDef(listingController, {
          minWidth: 300,
        }),
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController,
          {
            minWidth: 300,
          }
        ),
      ],
    },
  ];
}
